import { Deferred } from '@sail/utils';
import RpcChannel from '../internal/rpc-channel/channel';
import { createRpcHandshake } from '../internal/rpc-handshake/handshake';
import MessageChannel from '../internal/transport/MessageChannel';
import fromMessagePort from '../internal/transport/fromMessagePort';
import listen from '../internal/utils/listen';
import { v4 as uuid } from 'uuid';
/**
 * Factory function that creates a new RPC client and server. It returns an
 * object with two functions: `initServer` and `initClient`.
 *
 * The `initServer` function creates a new RPC server that listens for new
 * clients on the provided `transport`. Clients from a different `namespace`
 * will be ignored.  The server will create a new endpoint for each client that
 * connects to it.
 *
 * The `initClient` function creates a new RPC client that will try to find a
 * server on the same `namespace`.
 *
 * The `methods` parameter is an object containing the methods that will be
 * exposed from the endpoints created by the server. Due to the nature of RPC,
 * even if these methods are synchronous, they will be called asynchronously
 * and the results will be returned to clients as promises.
 *
 * The provided `transport` is used to perform the initial handshake between
 * the client and the server. After the handshake, the client and the server
 * will communicate using a private `MessageChannel` to avoid polluting the
 * provided `transport` and avoiding conflicts with other messages that may be
 * sent through the same `transport`.
 */ export default function createRpc({ namespace, methods, subscriptions, encoder }) {
    const { ack, synAck, isAckEvent, isSynAckEvent, syn, synServer, isSynEvent, isSynServerEvent, fin, finServer, isFinEvent, isFinServerEvent } = createRpcHandshake(namespace);
    return {
        initServer: ({ transport })=>{
            const serverId = uuid();
            const endpoints = new Map();
            let closing = null;
            // Listen for new client connections.
            const newClientListener = listen(transport, ({ data: event })=>{
                if (isSynEvent(event)) {
                    const { data } = event;
                    synAck(transport, {
                        clientId: data.clientId,
                        serverId
                    });
                }
                if (isAckEvent(event)) {
                    const { data } = event;
                    if (data.serverId === serverId) {
                        const { clientId, port } = data;
                        endpoints.set(clientId, new RpcChannel({
                            name: namespace,
                            transport: fromMessagePort(port),
                            methods,
                            subscriptions,
                            encoder
                        }));
                        port.start();
                    }
                }
            });
            // Listen for clients that are closing.
            const clientCloseListener = listen(transport, ({ data: event })=>{
                if (isFinEvent(event)) {
                    const { clientId } = event.data;
                    if (endpoints.has(clientId)) {
                        var _endpoints_get;
                        (_endpoints_get = endpoints.get(clientId)) === null || _endpoints_get === void 0 ? void 0 : _endpoints_get.close();
                        endpoints.delete(clientId);
                    }
                    // If there are no connected clients, and the server is closing,
                    // resolve the close promise.
                    if (endpoints.size === 0 && closing != null) {
                        clientCloseListener.stop();
                        closing === null || closing === void 0 ? void 0 : closing.resolve();
                    }
                }
            });
            // Announce that the server is ready to accept new clients.
            synServer(transport, {
                serverId
            });
            return {
                get id () {
                    return serverId;
                },
                close () {
                    closing = closing !== null && closing !== void 0 ? closing : new Deferred();
                    // Stop listening for new clients.
                    newClientListener.stop();
                    // Announce that the server is closing.
                    finServer(transport, {
                        serverId
                    });
                    // If there are no clients, resolve the close promise.
                    if (endpoints.size === 0) {
                        clientCloseListener.stop();
                        closing === null || closing === void 0 ? void 0 : closing.resolve();
                    }
                    // Don't close until all clients have disconnected.
                    return closing.promise;
                }
            };
        },
        initClient: ({ transport })=>{
            const clientId = uuid();
            let client = createClient(namespace, encoder);
            let serverId = null;
            const serverListener = listen(transport, ({ data: event })=>{
                if (isSynAckEvent(event)) {
                    const { data } = event;
                    if (data.clientId === clientId) {
                        // Stop listening for server handshake events.
                        serverListener.stop();
                        serverId = data.serverId;
                        ack(transport, {
                            clientId: data.clientId,
                            serverId: data.serverId,
                            port: client.port
                        }, // Transfer ownership of the port to the server
                        [
                            client.port
                        ]);
                    }
                }
                // If a new server is announced, and we're still looking for a server,
                // send a new syn event to request a new connection.
                if (isSynServerEvent(event)) {
                    syn(transport, {
                        clientId
                    });
                }
            });
            const serverCloseListener = listen(transport, ({ data: event })=>{
                if (isFinServerEvent(event)) {
                    const { data } = event;
                    if (serverId === data.serverId) {
                        serverId = null;
                        // Disconnect from the server.
                        fin(transport, {
                            clientId
                        });
                        // Create a new client.
                        client.endpoint.close();
                        client = createClient(namespace, encoder);
                        // Look for a new server.
                        serverListener.start();
                        syn(transport, {
                            clientId
                        });
                    }
                }
            });
            syn(transport, {
                clientId
            });
            return {
                get id () {
                    return clientId;
                },
                call (method, ...args) {
                    return client.endpoint.call(method, ...args);
                },
                watch (subscription, payload, callback) {
                    return client.endpoint.watch(subscription, payload, callback);
                },
                close () {
                    // Announce that the client is closing.
                    fin(transport, {
                        clientId
                    });
                    // Cleanup
                    serverListener.stop();
                    serverCloseListener.stop();
                    client.endpoint.close();
                }
            };
        }
    };
}
function createClient(source, encoder) {
    const { port1, port2 } = new MessageChannel();
    port1.start();
    return {
        endpoint: new RpcChannel({
            name: source,
            transport: fromMessagePort(port1),
            encoder
        }),
        port: port2
    };
}

//# sourceMappingURL=createRpc.js.map